import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { getCities } from "../services/guest";

const usePaginatedCities = (searchQuery = "", pageSize = 10) => {
  const { t } = useTranslation();
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(true);
  const [totalLoaded, setTotalLoaded] = useState(0);

  // Reset pagination when search query changes
  useEffect(() => {
    setCities([]);
    setCurrentPage(1);
    setHasNextPage(true);
    setTotalLoaded(0);
    setError(null);
  }, [searchQuery]);

  const fetchCities = useCallback(async (page = 1, isLoadMore = false) => {
    try {
      if (isLoadMore) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      setError(null);

      const response = await getCities(searchQuery, page, pageSize);
      
      if (page === 1) {
        setCities(response);
      } else {
        setCities(prevCities => [...prevCities, ...response]);
      }
      
      // Determine if there are more pages
      // If we received fewer items than pageSize, we've reached the end
      setHasNextPage(response.length === pageSize);
      setTotalLoaded(prev => page === 1 ? response.length : prev + response.length);
      
    } catch (err) {
      const errorMessage = t("ErrorFetchingCities") || "Error fetching cities";
      setError({ message: errorMessage, originalError: err });
      console.error("Error fetching cities:", err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [searchQuery, pageSize, t]);

  // Load initial data
  useEffect(() => {
    fetchCities(1, false);
  }, [fetchCities]);

  const loadMore = useCallback(() => {
    if (!loadingMore && hasNextPage) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      fetchCities(nextPage, true);
    }
  }, [currentPage, loadingMore, hasNextPage, fetchCities]);

  const refresh = useCallback(() => {
    setCurrentPage(1);
    fetchCities(1, false);
  }, [fetchCities]);

  return {
    cities,
    loading,
    loadingMore,
    error,
    hasNextPage,
    totalLoaded,
    loadMore,
    refresh
  };
};

export default usePaginatedCities;